import React, { useState, useMemo } from 'react';
import { calculate } from '../lib/calculations';

// ─── Volume range presets ────────────────────────────────────────────
//
// User picks one and the table re-renders with those quantity rows.
// "custom" pulls from a free-text input the user types (parsed below).
const VOLUME_PRESETS = {
  low:      { label: 'Low-volume',  desc: 'hobby / new shop',     volumes: [1, 3, 5, 8, 12, 20] },
  standard: { label: 'Standard',    desc: 'most shops',           volumes: [1, 5, 10, 25, 50, 100] },
  high:     { label: 'High-volume', desc: 'established / scaled', volumes: [10, 50, 100, 250, 500, 1000] },
};

// Reverse-planning: profit targets the user might shoot for.
// Per-row we compute "how many units do I need to sell to hit this target?"
const GOAL_TARGETS = [100, 500, 1000, 2500, 5000, 10000];

function parseCustomVolumes(text) {
  return text
    .split(/[,\s]+/)
    .map(s => parseInt(s, 10))
    .filter(n => Number.isFinite(n) && n >= 1)
    .sort((a, b) => a - b)
    .filter((v, i, arr) => arr.indexOf(v) === i)
    .slice(0, 8);
}

export default function SensitivityTable({ inputs, settings, setSettings, currencySymbol = '$' }) {
  const [planMode, setPlanMode] = useState('volume');     // 'volume' | 'goal'
  const [preset, setPreset] = useState('standard');       // 'low' | 'standard' | 'high' | 'custom'
  const [customVolText, setCustomVolText] = useState('');
  const [customQty, setCustomQty] = useState('');

  const isBulk = settings?.orderType === 'bulk';
  const updS = (patch) => { if (setSettings) setSettings(s => ({ ...s, ...patch })); };

  // Resolve the active volume list from the preset
  const volumes = useMemo(() => {
    if (preset === 'custom') {
      const parsed = parseCustomVolumes(customVolText);
      return parsed.length > 0 ? parsed : VOLUME_PRESETS.standard.volumes;
    }
    return VOLUME_PRESETS[preset]?.volumes || VOLUME_PRESETS.standard.volumes;
  }, [preset, customVolText]);

  // Volume-mode rows — re-runs calculate() per volume so it tracks settings
  // (country, offsite, bulk vs separate) live.
  const volumeRows = useMemo(() => {
    if (!inputs?.price || !settings) return [];
    return volumes.map(q => {
      const r = calculate({ ...inputs, quantity: q }, settings);
      return {
        quantity: q,
        revenue: r.revenue,
        totalCost: r.totalCost,
        profit: r.profit,
        profitPerItem: r.profitPerItem,
        margin: r.margin,
      };
    });
  }, [volumes, inputs, settings]);

  // "Try your own qty" custom row, prepended in volume mode
  const customRow = useMemo(() => {
    const q = parseInt(customQty, 10);
    if (!q || q < 1 || !inputs || !settings) return null;
    const r = calculate({ ...inputs, quantity: q }, settings);
    return {
      quantity: q,
      revenue: r.revenue,
      totalCost: r.totalCost,
      profit: r.profit,
      profitPerItem: r.profitPerItem,
      margin: r.margin,
    };
  }, [customQty, inputs, settings]);

  // Goal-mode rows — for each profit target, how many units must I sell?
  // We compute per-item profit at q=1 in separate-order mode (stable per-item
  // math) and divide. If per-item profit is ≤ 0, we surface a hint instead
  // of nonsense rows.
  const goalRows = useMemo(() => {
    if (planMode !== 'goal' || !inputs?.price || !settings) return null;
    const single = calculate({ ...inputs, quantity: 1 }, { ...settings, orderType: 'separate' });
    const ppi = single?.profitPerItem ?? 0;
    if (ppi <= 0) return { ppi, rows: [] };
    const rows = GOAL_TARGETS.map(target => {
      const units = Math.ceil(target / ppi);
      return {
        target,
        units,
        unitsPerWeek: units / (52 / 12), // 4.345 weeks per month
        unitsPerDay: units / 30,
        ppi,
      };
    });
    return { ppi, rows };
  }, [planMode, inputs, settings]);

  if (!inputs?.price) return null;

  const fmt = (v) => `${v < 0 ? '-' : ''}${currencySymbol}${Math.abs(v).toFixed(2)}`;

  // ─── Volume-mode row renderer ───────────────────────────
  const renderVolumeRow = (r, isCustom = false) => (
    <tr
      key={isCustom ? `custom-${r.quantity}` : r.quantity}
      className="border-b border-dashed border-ink/30 hover:bg-ink/5"
      style={isCustom ? { backgroundColor: '#fef9c3' } : undefined}
    >
      <td className="py-2 px-2 font-numbers text-lg" style={{ fontWeight: 600 }}>
        {isCustom && <span className="mr-1" aria-hidden="true">⭐</span>}
        {r.quantity}
      </td>
      <td className="text-right py-2 px-2 font-numbers text-emerald-700" style={{ fontWeight: 600 }}>{fmt(r.revenue)}</td>
      <td className="text-right py-2 px-2 font-numbers text-orange-700" style={{ fontWeight: 600 }}>{fmt(r.totalCost)}</td>
      <td className={`text-right py-2 px-2 font-numbers text-lg ${
        r.profit >= 0 ? 'text-emerald-700' : 'text-alert'
      }`} style={{ fontWeight: 700 }}>
        {fmt(r.profit)}
      </td>
      <td className="text-right py-2 px-2 font-numbers text-ink/72">{fmt(r.profitPerItem)}</td>
      <td className={`text-right py-2 px-2 font-numbers ${
        r.margin >= 30 ? 'text-emerald-700' :
        r.margin >= 15 ? 'text-amber-700' :
        'text-alert'
      }`} style={{ fontWeight: 600 }}>
        {r.margin.toFixed(1)}%
      </td>
    </tr>
  );

  // ─── Goal-mode row renderer ─────────────────────────────
  const renderGoalRow = (r) => {
    const fmtUnits = (n) => n >= 100 ? Math.round(n).toString() : n.toFixed(1);
    return (
      <tr key={r.target} className="border-b border-dashed border-ink/30 hover:bg-ink/5">
        <td className="py-2 px-2 font-numbers text-lg" style={{ fontWeight: 700, color: '#7e22ce' }}>
          {currencySymbol}{r.target.toLocaleString('en-US')}
        </td>
        <td className="text-right py-2 px-2 font-numbers text-lg" style={{ fontWeight: 700 }}>
          {r.units.toLocaleString('en-US')}
        </td>
        <td className="text-right py-2 px-2 font-numbers text-ink/82" style={{ fontWeight: 600 }}>
          {fmtUnits(r.units)}
        </td>
        <td className="text-right py-2 px-2 font-numbers text-ink/82" style={{ fontWeight: 600 }}>
          {fmtUnits(r.unitsPerWeek)}
        </td>
        <td className="text-right py-2 px-2 font-numbers text-ink/72" style={{ fontWeight: 600 }}>
          {fmtUnits(r.unitsPerDay)}
        </td>
      </tr>
    );
  };

  // ─── JSX ────────────────────────────────────────────────
  return (
    <div className="sketch-card">
      <h2 className="font-display text-2xl text-ink mb-1">🔮 Volume Sandbox</h2>
      <p className="font-note text-note text-ink/85 mb-4">
        <span className="hl-yellow">
          {planMode === 'goal'
            ? 'Pick a profit goal — see how many units you need to sell, broken down per month, week, and day.'
            : isBulk
              ? 'One bulk order, scaling the quantity. Bulk pays one shipping + one fixed processing fee no matter the quantity, so margins climb fast.'
              : 'Same price, different volume. Each row = that many separate buyers; fixed fees stack on every order.'}
        </span>
      </p>

      {/* ─── Mode toggles row — Order type (volume mode only) + Plan from ───
          Uses auto-fit so the two toggles stack vertically on narrow viewports
          instead of cramming side-by-side. */}
      <div
        className="mb-3"
        style={{
          display: 'grid',
          gap: '0.7rem',
          gridTemplateColumns: (setSettings && planMode === 'volume')
            ? 'repeat(auto-fit, minmax(220px, 1fr))'
            : '1fr',
        }}
      >
        {setSettings && planMode === 'volume' && (
          <ControlBlock label="🛒 Order type">
            <SegmentedToggle
              value={isBulk ? 'bulk' : 'separate'}
              onChange={(v) => updS({ orderType: v })}
              options={[
                { value: 'separate', label: '👥 Separate', activeColor: '#1e3a8a' },
                { value: 'bulk',     label: '📦 Bulk',     activeColor: '#1e3a8a' },
              ]}
            />
          </ControlBlock>
        )}
        <ControlBlock label="🎯 Plan from">
          <SegmentedToggle
            value={planMode}
            onChange={setPlanMode}
            options={[
              { value: 'volume', label: '📊 Volume → Profit', activeColor: '#1e3a8a', title: 'Pick volumes, see profit' },
              { value: 'goal',   label: '💰 Profit → Volume', activeColor: '#7e22ce', title: 'Pick a profit target, see units needed' },
            ]}
          />
        </ControlBlock>
      </div>

      {/* ─── Volume range + Try qty (volume mode only) ─── */}
      {planMode === 'volume' && (
        <div className="mb-4">
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.85rem', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 240px', minWidth: 0 }}>
              <label className="font-sketch font-bold text-label" style={{ fontSize: '0.86rem' }}>
                📊 Volume range
              </label>
              <div style={{ display: 'flex', gap: '0.32rem', flexWrap: 'wrap', marginTop: '0.25rem' }}>
                {Object.entries(VOLUME_PRESETS).map(([key, p]) => (
                  <PresetChip
                    key={key}
                    active={preset === key}
                    onClick={() => setPreset(key)}
                    title={p.desc}
                    label={p.label}
                  />
                ))}
                <PresetChip
                  active={preset === 'custom'}
                  onClick={() => setPreset('custom')}
                  title="Type your own quantities"
                  label="✏️ Custom"
                />
              </div>
            </div>
            <div style={{ flex: '0 0 auto' }}>
              <label className="font-sketch font-bold text-label" style={{ fontSize: '0.86rem' }}>
                ⭐ Try your own qty
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.25rem' }}>
                <input
                  type="number"
                  value={customQty}
                  onChange={(e) => setCustomQty(e.target.value)}
                  className="sketch-input mt-0"
                  style={{ maxWidth: '120px' }}
                  min="1"
                  step="1"
                  placeholder="e.g. 17"
                />
                {customQty && (
                  <button
                    onClick={() => setCustomQty('')}
                    className="font-note text-note text-ink/68 hover:text-alert transition-colors"
                    style={{ fontSize: '0.85rem' }}
                  >
                    clear
                  </button>
                )}
              </div>
            </div>
          </div>
          {preset === 'custom' && (
            <div style={{ marginTop: '0.6rem' }}>
              <input
                type="text"
                value={customVolText}
                onChange={(e) => setCustomVolText(e.target.value)}
                placeholder="e.g. 7, 15, 30, 75, 150 (commas or spaces)"
                className="sketch-input mt-0"
                style={{ maxWidth: '420px' }}
                maxLength={120}
              />
              <p className="font-body text-xs text-ink/72 mt-1">
                Up to 8 quantities. Auto-sorted ascending.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Goal-mode info strip */}
      {planMode === 'goal' && goalRows && (
        <div
          className="mb-3"
          style={{
            padding: '0.55rem 0.75rem',
            background: 'rgba(126, 34, 206, 0.06)',
            border: '1px solid rgba(126, 34, 206, 0.3)',
            borderRadius: '0.5rem',
          }}
        >
          {goalRows.ppi <= 0 ? (
            <p className="font-body text-sm" style={{ color: '#9f1239', fontWeight: 600, margin: 0 }}>
              ⚠️ Each sale earns {fmt(goalRows.ppi)} per item — can't reach a positive profit goal at this price. Re-price first.
            </p>
          ) : (
            <p className="font-body text-sm text-ink" style={{ margin: 0, lineHeight: 1.4 }}>
              Each sale earns{' '}
              <strong style={{ color: '#047857' }}>{fmt(goalRows.ppi)}</strong>{' '}
              of profit. Goals below assume separate orders.
            </p>
          )}
        </div>
      )}

      {/* The table */}
      <div className="overflow-x-auto">
        <div className="table-scroll">
          {planMode === 'volume' ? (
            <table className="w-full font-sketch text-body">
              <thead>
                <tr className="border-b-2 border-ink">
                  <th className="text-left py-2 px-2 font-display text-sm uppercase tracking-wider text-ink/82">Qty</th>
                  <th className="text-right py-2 px-2 font-display text-sm uppercase tracking-wider text-ink/82">Revenue</th>
                  <th className="text-right py-2 px-2 font-display text-sm uppercase tracking-wider text-ink/82">Costs</th>
                  <th className="text-right py-2 px-2 font-display text-sm uppercase tracking-wider text-ink/82">Profit</th>
                  <th className="text-right py-2 px-2 font-display text-sm uppercase tracking-wider text-ink/82">Per Item</th>
                  <th className="text-right py-2 px-2 font-display text-sm uppercase tracking-wider text-ink/82">Margin</th>
                </tr>
              </thead>
              <tbody>
                {customRow && renderVolumeRow(customRow, true)}
                {volumeRows.map((r) => renderVolumeRow(r))}
              </tbody>
            </table>
          ) : (
            goalRows && goalRows.rows.length > 0 && (
              <table className="w-full font-sketch text-body">
                <thead>
                  <tr className="border-b-2 border-ink">
                    <th className="text-left py-2 px-2 font-display text-sm uppercase tracking-wider text-ink/82">Profit Goal</th>
                    <th className="text-right py-2 px-2 font-display text-sm uppercase tracking-wider text-ink/82">Units Needed</th>
                    <th className="text-right py-2 px-2 font-display text-sm uppercase tracking-wider text-ink/82">/ Month</th>
                    <th className="text-right py-2 px-2 font-display text-sm uppercase tracking-wider text-ink/82">/ Week</th>
                    <th className="text-right py-2 px-2 font-display text-sm uppercase tracking-wider text-ink/82">/ Day</th>
                  </tr>
                </thead>
                <tbody>
                  {goalRows.rows.map((r) => renderGoalRow(r))}
                </tbody>
              </table>
            )
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components for the controls grid ─────────────────────────────

// Labeled control wrapper — keeps label spacing consistent across cells.
function ControlBlock({ label, children }) {
  return (
    <div>
      <label className="font-sketch font-bold text-label" style={{ fontSize: '0.86rem' }}>
        {label}
      </label>
      <div style={{ marginTop: '0.25rem' }}>{children}</div>
    </div>
  );
}

// Segmented two-up toggle (used for Order type and Plan from). Slimmer
// padding/border than the previous bespoke toggles so two of these fit
// comfortably side-by-side in the controls grid.
function SegmentedToggle({ value, onChange, options }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        border: '1.5px solid #1a1a2e',
        borderRadius: '0.6rem',
        overflow: 'hidden',
        boxShadow: '0 1px 2px rgba(26,26,46,0.05)',
      }}
    >
      {options.map((opt, i) => {
        const active = value === opt.value;
        const isLeft = i === 0;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            title={opt.title || ''}
            style={{
              padding: '0.42rem 0.7rem',
              fontFamily: '"Inter", sans-serif',
              fontSize: '0.92rem',
              fontWeight: 600,
              cursor: 'pointer',
              border: 'none',
              borderRight: isLeft ? '1px solid rgba(26, 26, 46, 0.12)' : 'none',
              backgroundColor: active ? opt.activeColor : '#fefefe',
              color: active ? '#fefefe' : '#1e293b',
              transition: 'all 0.12s',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// Compact preset chip used in the Volume range row.
function PresetChip({ active, onClick, title, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      style={{
        padding: '0.32rem 0.7rem',
        background: active ? '#1a1a2e' : '#fefdf8',
        color: active ? '#fefdf8' : '#1a1a2e',
        border: `1.5px solid ${active ? '#1a1a2e' : 'rgba(26,26,46,0.18)'}`,
        borderRadius: '0.5rem',
        fontFamily: 'Inter, sans-serif',
        fontSize: '0.86rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.12s',
      }}
    >
      {label}
    </button>
  );
}
